<template>
  <form
    ref="searchForm"
    action="/search"
    autocomplete="off"
  >
    <div
      class="search-form-container appsumo-dropdown-outer-container"
      @focusin="showOverlay"
      @focusout="hideOverlay"
    >
      <label
        :for="id"
        class="sr-only"
      >
        Search
      </label>
      <!--
        Decomposed input/value to make autocomplete work
        Using v-model doesn't work with Android Keyboards (expected behaviour)
        https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
      -->
      <input
        :id="id"
        ref="input"
        type="text"
        placeholder="Search products"
        name="query"
        autocomplete="off"
        maxlength="200"
        required
        :value="searchInput"
        @input="useAutocompleteStore($event.target.value)"
        @keydown="onKeyDown"
      >
      <button
        v-if="searchInput"
        type="button"
        class="btn-search-clear"
        aria-label="Clear search"
        @click="onClearSearch"
        @keydown.enter="onClearSearch"
      >
        <font-awesome-icon icon="times" />
      </button>
      <button
        id="search-submit"
        type="submit"
        aria-label="Search with AppSumo"
        class="flex-shrink-0"
        :class="{ 'd-none d-lg-block': searchInput }"
      >
        <font-awesome-icon icon="search" />
      </button>
      <search-autocomplete
        :options="options"
        :original-query="originalInput"
        :query="searchInput"
        :highlight-index="highlightOptionIndex"
        @on-click-option="onClickSearchOption"
      />
    </div>
  </form>
</template>

<script>
import get from 'lodash/get';
import { extractBrowse } from '@/util/global';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SearchAutocomplete from './SearchAutocomplete.vue';

export default {
  name: 'SearchDropdownForm',
  components: {
    FontAwesomeIcon,
    SearchAutocomplete,
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    const qs = this.$route?.query || {};
    const params = extractBrowse(this.$route?.params.pathMatch);

    return {
      highlightOptionIndex: -1,
      originalInput: '',
      searchInput: '',
      formattedOptions: [],
      type: qs.group || params.group || null,
    };
  },
  computed: {
    isBodyOverlayVisible() {
      return this.$store.getters.showBodyOverlay;
    },
  },
  watch: {
    '$route.query': function () {
      const params = extractBrowse(this.$route?.params.pathMatch);

      if (this.$route.query.group) {
        this.type = this.$route.query.group;
      } else if (params.group) {
        this.type = params.group;
      } else {
        this.type = null;
      }

      if (this.$route.query.query !== this.searchInput) {
        this.searchInput = this.$route.query.query;
        this.$store.commit('setAutocompleteQuery', this.searchInput);
      }
    },
    type() {
      this.$store.commit('setAutocompleteGroup', this.type);
      this.rewriteFormAction();
    },
  },
  created() {
    this.hideOverlayTimeoutId = null;
    this.rewriteFormAction();
  },
  mounted() {
    const searchQuery = get(this, ['$route', 'query', 'query'], '').trim();
    if (searchQuery && searchQuery.length) {
      this.searchInput = searchQuery;
      this.originalInput = searchQuery;
      // Without setSearchQuery set, cannot clear results properly after page load
      this.$store.commit('setAutocompleteQuery', this.searchInput);
    }

    if (this.type) {
      this.$store.commit('setAutocompleteGroup', this.type);
      this.rewriteFormAction();
    }

    // Set the initial window width
    this.currentWindowWidth = window.innerWidth;
  },
  beforeUnmount() {
    if (this.hideOverlayTimeoutId) {
      // clear the hide timeout if one was set. No-op if one is not set.
      clearTimeout(this.hideOverlayTimeoutId);

      // toggle the overlay off with no timeout when unmounting the component
      this.toggleOverlayVisibility(false);
    }

    // Remove the resize event listener
    document.body.removeEventListener('touchmove', this.preventTouchMove, false);
  },
  methods: {
    rewriteFormAction() {
      if (this.$refs && this.$refs.searchForm) {
        if (this.type) {
          this.$refs.searchForm.action = `/${this.type}`;
        } else {
          this.$refs.searchForm.action = '/search';
        }
      }
    },
    isInputEmpty() {
      return !(this.searchInput && this.searchInput.length);
    },
    addActivity() {
      const userId = get(this, ['$store', 'state', 'session', 'user', 'id']);

      if (this.searchInput && userId && this.$activity) {
        this.$activity.add({
          actor: `User:${userId}`,
          verb: 'deal_search',
          full_text: this.searchInput,
        });
      }
    },
    onClickSearchOption(option) {
      if (option.slug) {
        window.location = `/products/${option.slug}?query=${encodeURIComponent(this.searchInput || option.value)}`;
        return;
      }

      this.searchInput = option.value;

      this.$nextTick(() => {
        const searchForm = get(this, ['$refs', 'searchForm']);

        if (searchForm?.submit) {
          searchForm.submit();
        }
      });
    },
    onKeyDown(e) {
      const {
        highlightOptionIndex: optIndex,
        options,
        originalInput,
      } = this;

      if (!options.length) return;

      if (e.keyCode === 40) {
        this.highlightOptionIndex = (optIndex < options.length)
          ? optIndex + 1
          : 0;

        const newOption = options[this.highlightOptionIndex]?.public_name;

        if (newOption) {
          this.searchInput = newOption;
        } else {
          this.searchInput = originalInput;
        }
      } else if (e.keyCode === 38) {
        this.highlightOptionIndex = (optIndex < 1)
          ? options.length
          : optIndex - 1;

        const newOption = options[this.highlightOptionIndex]?.public_name;

        if (newOption) {
          this.searchInput = newOption;
        } else {
          this.searchInput = originalInput;
        }

        // Hack to get the input caret to move to the end
        this.$nextTick(() => {
          setTimeout(() => {
            e.target.setSelectionRange(255, 255);
          }, 10);
        });
      }
    },
    onClearSearch() {
      this.useAutocompleteStore('');
      this.$refs.input.focus();
    },
    useAutocompleteStore(value) {
      this.searchInput = value;

      this.originalInput = this.searchInput;
      this.$store.commit('setAutocompleteQuery', this.searchInput);
    },
    /**
     * Show an overlay when search is activated (focus or product-type dropdown)
     *
     * jw: the idea with these overlay functions is to isolate the management of the timeout
     *     function from the code that actually makes the overlay visible or not.
     */
    showOverlay() {
      // jw: clear the hide timeout if one was set. No-op if one is not set.
      clearTimeout(this.hideOverlayTimeoutId);

      // showing actions will happen immediatly with no delay, so clearing timeout
      this.hideOverlayTimeoutId = null;

      // Call to actually toggle the overlay
      this.toggleOverlayVisibility(true);
    },
    /**
     * Remove an overlay when search is deactivated
     */
    hideOverlay() {
      // jw: clear the hide timeout if one was set. No-op if one is not set.
      clearTimeout(this.hideOverlayTimeoutId);

      // hiding actions will happen on a 150ms delay to account for the delay between
      // search focus and opening the product-type dropdown that clears focus
      this.hideOverlayTimeoutId = setTimeout(
        () => this.toggleOverlayVisibility(false),
        150,
      );
    },
    /**
     * Toggle the overlay visibility on/off
     * @param {boolean} show  whether to show or hide the overlay
     */
    toggleOverlayVisibility(show) {
      document.body.classList.toggle('overflow-hidden', show);

      // jw: next, if the overlay is already in the state we want we can short out.
      if (show === this.isBodyOverlayVisible) {
        return;
      }

      // jw: finally, we can just update the classes and call it a day.
      if (show) {
        this.$store.dispatch('setShowBodyOverlay', true);

        // See method for more info. Using passive false as recommended for touch events on MDN docs
        // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#improving_scrolling_performance_with_passive_listeners
        document.body.addEventListener('touchmove', this.preventTouchMove, { passive: false });
      } else {
        const newNavbar = document.getElementsByClassName('new-navbar');

        // dh: if new navbar is open then don't close overlay
        if (newNavbar && newNavbar.length > 0 && newNavbar[0].classList.contains('nav-open')) {
          return;
        }

        this.$store.dispatch('setShowBodyOverlay', false);
        // See method for more info
        document.body.removeEventListener('touchmove', this.preventTouchMove, false);
      }
    },
    /**
     * Prevent scrolling for mobile using only CSS doesn not work on iOS with Safari,
     * so we disable the touchmove to prevent scrolling while having the overlay enable
     * https://hynchrstn.wordpress.com/2021/01/12/disable-ios-scrolling-overflow-hidden-is-not-working/
     */
    preventTouchMove(event) {
      event.preventDefault();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/components/navbar';

  .sumo-browse-page .navbar .search-container.search-container-mobile {
    margin-top: 3px;
    max-height: 50px;
    margin-bottom: 10px;
  }

  .appsumo-dropdown-outer-container {
    position: relative;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

    &:focus-within {
      .search-dropdown-contents.display-dropdown {
        opacity: 1;
        z-index: 99999;
        visibility: initial !important;
      }
    }

    &:active {
      box-shadow: 0 0 10px rgba(2, 117, 216, 0.3);
    }
  }

  .welcome-mat-search {
    color: #ffff;
  }
</style>
